import React, { useState, useEffect, useRef, useCallback } from "react";
import { getWebSocketUrl, getApiUrl } from "../../config/websocket";
import EmojiPicker from "../EmojiPicker/EmojiPicker";
import "./TextChat.scss";

//  Scroll helper 
function useScrollHold(ref, dir) {
  const intervalRef = useRef(null);
  const start = () => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(() => {
      ref.current?.scrollBy({ top: dir * 40, behavior: "smooth" });
    }, 60);
  };
  const stop = () => { clearInterval(intervalRef.current); intervalRef.current = null; };
  return { onMouseDown: start, onMouseUp: stop, onMouseLeave: stop, onTouchStart: start, onTouchEnd: stop };
}

const normalizeUser = (user) => {
  if (!user) return { username: "Unknown", avatar: null };
  if (typeof user === "string") return { username: user, avatar: null };
  return { username: user.username || "Unknown", avatar: user.avatar || null };
};

//  Frequent emoji helpers 
const FREQ_KEY = "tc_freq_emojis";
const QUICK_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🔥", "👏", "🎉", "💯", "😍"];

function getFreqEmojis() {
  try {
    const raw = localStorage.getItem(FREQ_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    // Filter out empty/invalid entries that may have been stored when QUICK_EMOJIS was empty
    return Object.entries(parsed)
      .filter(([emoji]) => emoji && emoji.trim().length > 0)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([emoji]) => emoji);
  } catch { return []; }
}

function trackFreqEmoji(emoji) {
  if (!emoji || !emoji.trim()) return;
  try {
    const raw = localStorage.getItem(FREQ_KEY);
    const map = raw ? JSON.parse(raw) : {};
    // Remove any blank/invalid keys that snuck in
    Object.keys(map).forEach(k => { if (!k || !k.trim()) delete map[k]; });
    map[emoji] = (map[emoji] || 0) + 1;
    localStorage.setItem(FREQ_KEY, JSON.stringify(map));
  } catch {}
}

//  VoiceMessage 
const WAVE_BARS = 33;
const VoiceMessage = ({ url }) => {
  const audioRef  = useRef(null);
  const animRef   = useRef(null);
  const [playing,  setPlaying]  = useState(false);
  const [current,  setCurrent]  = useState(0);
  const [duration, setDuration] = useState(0);
  const [liveH,    setLiveH]    = useState(null);

  const fmt = (s) => {
    if (!s || !isFinite(s) || isNaN(s)) return '0:00';
    return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  };

  // static waveform — speech-envelope shape
  const staticH = Array.from({ length: WAVE_BARS }, (_, i) => {
    const n = i / WAVE_BARS;
    return Math.max(8, 10 + Math.sin(n * Math.PI) * 60 + Math.abs(Math.sin(i * 2.1)) * 18 + Math.cos(i * 3.7) * 7);
  });
  const maxStatic = Math.max(...staticH);

  // animate bars randomly while playing
  useEffect(() => {
    if (playing) {
      const tick = () => {
        setLiveH(staticH.map(h => {
          const base = (h / maxStatic) * 100;
          return Math.max(8, Math.min(100, base + (Math.random() - 0.5) * 30));
        }));
        animRef.current = setTimeout(tick, 120);
      };
      animRef.current = setTimeout(tick, 0);
      return () => clearTimeout(animRef.current);
    } else {
      setLiveH(null);
    }
  }, [playing]); // eslint-disable-line

  const progress = duration > 0 ? current / duration : 0;
  const heights  = liveH || staticH.map(h => (h / maxStatic) * 100);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play(); setPlaying(true); }
  };

  const seek = (e) => {
    const rect  = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    if (audioRef.current) { audioRef.current.currentTime = ratio * duration; setCurrent(ratio * duration); }
  };

  return (
    <div className={`text-chat__voice-msg${playing ? ' playing' : ''}`}>
      <audio
        ref={audioRef}
        src={url}
        onTimeUpdate={e => setCurrent(e.target.currentTime)}
        onDurationChange={e => setDuration(e.target.duration)}
        onEnded={() => { setPlaying(false); setCurrent(0); }}
        preload="metadata"
      />

      <button className="text-chat__voice-msg-play" onClick={toggle} aria-label={playing ? 'Пауза' : 'Воспроизвести'}>
        <div className="text-chat__voice-msg-play-ripple" />
        {playing
          ? <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
          : <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M8 5v14l11-7z"/></svg>
        }
      </button>

      <div className="text-chat__voice-msg-body">
        <div className="text-chat__voice-msg-wave" onClick={seek}>
          {heights.map((h, i) => (
            <span
              key={i}
              className={`text-chat__voice-msg-bar${i / WAVE_BARS < progress ? ' filled' : ''}${playing ? ' live' : ''}`}
              style={{ height: `${h}%` }}
            />
          ))}
          <span className="text-chat__voice-msg-thumb" style={{ left: `${progress * 100}%` }} />
        </div>
        <div className="text-chat__voice-msg-foot">
          <span className="text-chat__voice-msg-time">{playing || current > 0 ? fmt(current) : fmt(duration)}</span>
          <span className="text-chat__voice-msg-mic">
            <svg viewBox="0 0 24 24" fill="currentColor" width="11" height="11"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/></svg>
            Голосовое
          </span>
        </div>
      </div>
    </div>
  );
};

//  Component 
//  Circle video player with custom play/pause overlay
const CircleVideoPlayer = ({ url, name }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };

  return (
    <div className="text-chat__circle-video-wrap" onClick={toggle}>
      <video
        ref={videoRef}
        src={url}
        className="text-chat__circle-video"
        playsInline
        preload="metadata"
        loop
        onEnded={() => setPlaying(false)}
      />
      {!playing && (
        <div className="text-chat__circle-video-overlay">
          <svg viewBox="0 0 24 24" fill="currentColor" width="44" height="44"><path d="M8 5v14l11-7z"/></svg>
        </div>
      )}
      <a
        href={url}
        download={name}
        className="text-chat__circle-video-dl"
        target="_blank"
        rel="noopener noreferrer"
        title="Скачать"
        onClick={e => e.stopPropagation()}
      >
        <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>
      </a>
    </div>
  );
};

//  Component 
//  Reaction picker — defined outside TextChat so it never remounts on render 
const ReactionPicker = ({ msgId, onReact, onClose }) => {
  const [showMoreEmojis, setShowMoreEmojis] = useState(false);
  const pickerRef = useRef(null);
  const freqEmojis = getFreqEmojis();
  const combined = [...new Set([...freqEmojis, ...QUICK_EMOJIS])].filter(em => em && em.trim().length > 0);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) onClose();
    };
    // Use capture phase so it fires before any bubbling stops it
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [onClose]);

  return (
    <div className="text-chat__reaction-picker" ref={pickerRef}>
      <div className="text-chat__reaction-picker-grid">
        {combined.map(em => (
          <button key={em} className="text-chat__reaction-picker-btn" onClick={() => onReact(msgId, em)}>{em}</button>
        ))}
        <button
          className="text-chat__reaction-picker-btn text-chat__reaction-picker-more"
          onClick={() => setShowMoreEmojis(v => !v)}
          title="Все эмодзи"
        >
          {showMoreEmojis ? "✕" : "···"}
        </button>
      </div>
      {showMoreEmojis && (
        <div className="text-chat__reaction-full-picker" data-lenis-prevent>
          <EmojiPicker inline onSelect={(emoji) => onReact(msgId, emoji)} />
        </div>
      )}
    </div>
  );
};

const TextChat = ({ roomSlug, currentUser, voiceMicActive = false }) => {
  const [ws, setWs] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hoveredMsgId, setHoveredMsgId] = useState(null);
  const [reactionPickerMsgId, setReactionPickerMsgId] = useState(null);
  const [replyTo, setReplyTo] = useState(null); // { id, username, content }
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const scrollBehaviorRef = useRef('instant'); // 'instant' | 'smooth' | 'none'
  const holdUp = useScrollHold(messagesContainerRef, -1);
  const holdDown = useScrollHold(messagesContainerRef, 1);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  // Circle video/voice recording
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingType, setRecordingType] = useState('video'); // 'video' | 'audio'
  const mediaRecorderRef = useRef(null);
  const recordingStreamRef = useRef(null);
  const recordingPreviewRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const [mobileRecordMode, setMobileRecordMode] = useState('audio'); // 'audio' | 'video'
  const mobileLongPressRef = useRef(null);
  const [recordingError, setRecordingError] = useState(null); // 'busy' | null

  // Stable callback ref — assigned ONCE when the <video> mounts, not on every re-render
  const recordingPreviewCallback = useCallback((el) => {
    if (el && recordingStreamRef.current) {
      el.srcObject = recordingStreamRef.current;
      el.play().catch(() => {});
    }
  }, []); // empty deps → same function reference → React won't re-fire on timer ticks

  const fetchMessages = useCallback(async (beforeId = null) => {
    const token = localStorage.getItem("token");
    let url = getApiUrl(`/api/community/chat-rooms/${roomSlug}/messages/?limit=30`);
    if (beforeId) url += `&before_id=${beforeId}`;
    const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!response.ok) throw new Error("Failed to fetch messages");
    return await response.json();
  }, [roomSlug]);

  useEffect(() => {
    if (!roomSlug || !currentUser) return;
    scrollBehaviorRef.current = 'instant';
    setMessages([]);
    const loadHistory = async () => {
      setIsLoading(true);
      try {
        const data = await fetchMessages();
        const results = data.results || (Array.isArray(data) ? data : []);
        scrollBehaviorRef.current = 'instant';
        setMessages(results);
        setHasMore(data.has_more || false);
      } catch (error) {
        console.error("Failed to load message history:", error);
      } finally {
        setIsLoading(false);
        // Повторные прокрутки — изображения/медиа грузятся после первого scrollToBottom
        // и увеличивают высоту контейнера, поэтому нужно догонять несколько раз
        const forceScrollDown = () => {
          const c = messagesContainerRef.current;
          if (c) c.scrollTop = c.scrollHeight;
        };
        setTimeout(forceScrollDown, 50);
        setTimeout(forceScrollDown, 150);
        setTimeout(forceScrollDown, 350);
        setTimeout(forceScrollDown, 700);
      }
    };
    loadHistory();

    const token = localStorage.getItem("token");
    const wsUrl = getWebSocketUrl(`ws/chat/${roomSlug}/`) + (token ? `?token=${token}` : "");
    const websocket = new WebSocket(wsUrl);
    websocket.onopen = () => setIsConnected(true);
    websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.error) return;
      if (data.type === "reaction_update") {
        setMessages(prev => prev.map(m => m.id === data.message_id ? { ...m, reactions: data.reactions } : m));
        return;
      }
      if (data.type === "message_deleted") {
        setMessages(prev => prev.map(m => m.id === data.message_id ? { ...m, content: "[Сообщение удалено]", is_deleted: true } : m));
        return;
      }
      const newMsg = {
        id: data.id || Date.now(),
        user: data.user,
        content: data.content,
        created_at: data.created_at || new Date().toISOString(),
        reply_to: data.reply_to || null,
        isOptimistic: false,
      };
      scrollBehaviorRef.current = 'smart';
      setMessages(prev => {
        if (data.id && prev.some(m => !m.isOptimistic && m.id === data.id)) return prev;
        const isFromMe = normalizeUser(data.user).username === currentUser?.username;
        if (isFromMe) {
          const idx = prev.findIndex(m => m.isOptimistic && m.content === data.content);
          if (idx !== -1) { const next = [...prev]; next[idx] = newMsg; return next; }
        }
        return [...prev, newMsg];
      });
    };
    websocket.onclose = () => setIsConnected(false);
    websocket.onerror = () => setIsConnected(false);
    setWs(websocket);
    return () => websocket.close();
  }, [roomSlug, currentUser, fetchMessages]);

  // ResizeObserver: пока идёт начальная загрузка ('instant') — следим за ростом
  // контейнера (картинки/видео подгружаются) и тянем скролл вниз автоматически
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    let pinned = true; // начинаем в режиме "прижать к низу"
    const observer = new ResizeObserver(() => {
      if (pinned) {
        container.scrollTop = container.scrollHeight;
      }
    });
    observer.observe(container);
    // Отпускаем пин после того как пользователь сам прокручивает вверх
    const onScroll = () => {
      const distFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      pinned = distFromBottom < 60;
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      observer.disconnect();
      container.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    const behavior = scrollBehaviorRef.current;
    if (behavior === 'none') { scrollBehaviorRef.current = 'smart'; return; }
    const container = messagesContainerRef.current;
    const end = messagesEndRef.current;
    if (!end || messages.length === 0) return;
    if (behavior === 'instant') {
      // Jump to bottom — works on all browsers including mobile
      const scrollToBottom = () => {
        if (container) {
          container.scrollTop = container.scrollHeight;
        } else if (end) {
          end.scrollIntoView({ block: 'end' });
        }
      };
      scrollToBottom();
      // Double rAF ensures DOM + images have rendered (critical for mobile)
      requestAnimationFrame(() => requestAnimationFrame(scrollToBottom));
      scrollBehaviorRef.current = 'smart';
    } else {
      // Smart: only scroll if user is near the bottom
      if (container) {
        const distFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        if (distFromBottom < 200) end.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }
  }, [messages]);

  const loadMore = async () => {
    if (loadingMore || !hasMore || messages.length === 0) return;
    setLoadingMore(true);
    const oldestId = messages[0]?.id;
    try {
      const data = await fetchMessages(oldestId);
      const results = data.results || [];
      const container = messagesContainerRef.current;
      const prevScrollHeight = container?.scrollHeight || 0;
      scrollBehaviorRef.current = 'none';
      setMessages(prev => [...results, ...prev]);
      setHasMore(data.has_more || false);
      requestAnimationFrame(() => {
        if (container) container.scrollTop = container.scrollHeight - prevScrollHeight;
      });
    } catch (e) {
      console.error("Failed to load more:", e);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (container && container.scrollTop < 80) loadMore();
  };

  const sendMessage = () => {
    if (!inputValue.trim() || !ws || ws.readyState !== WebSocket.OPEN) return;
    const tempMessage = {
      id: `temp-${Date.now()}`,
      user: currentUser?.username,
      content: inputValue,
      created_at: new Date().toISOString(),
      reply_to: replyTo ? { id: replyTo.id, username: replyTo.username, content: replyTo.content } : null,
      isOptimistic: true,
    };
    setMessages(prev => [...prev, tempMessage]);
    ws.send(JSON.stringify({ message: inputValue, reply_to_id: replyTo?.id || null }));
    setInputValue("");
    setReplyTo(null);
    if (inputRef.current) inputRef.current.style.height = "auto";
    inputRef.current?.focus();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
    if (e.key === "Escape" && replyTo) setReplyTo(null);
  };

  const handleReply = (msg) => {
    const { username } = normalizeUser(msg.user);
    setReplyTo({ id: msg.id, username, content: msg.content });
    inputRef.current?.focus();
  };

  const handleDeleteMessage = async (msgId) => {
    const token = localStorage.getItem("token");
    try {
      await fetch(getApiUrl(`/api/community/chat-messages/${msgId}/soft_delete/`), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, content: "[Сообщение удалено]", is_deleted: true } : m));
    } catch (_) {}
  };

  const handleReact = async (messageId, emoji) => {
    trackFreqEmoji(emoji);
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(getApiUrl(`/api/community/chat-messages/${messageId}/react/`), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ emoji }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => prev.map(m => m.id === messageId ? { ...m, reactions: data.reactions } : m));
      }
    } catch (_) {}
    setReactionPickerMsgId(null);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const parseFileMessage = (content) => {
    if (typeof content !== "string" || !content.startsWith("__FILE__:")) return null;
    try { return JSON.parse(content.slice(9)); } catch { return null; }
  };

  const isImageFile = (name, url) => {
    const imageExts = /\.(jpg|jpeg|png|gif|webp|bmp|svg|avif)$/i;
    if (name && imageExts.test(name)) return true;
    if (url && imageExts.test(url.split('?')[0])) return true;
    return false;
  };

  const isVideoFile = (name, url) => {
    const videoExts = /\.(mp4|mov|avi|mkv|m4v)$/i;
    if (name && videoExts.test(name)) return true;
    // webm is video only if filename doesn't start with audio_ or video_message_
    if (name && /\.webm$/i.test(name) && !/^audio_/i.test(name) && !/^video_message_/i.test(name)) return true;
    if (!name && url && /\.webm$/i.test(url.split('?')[0])) return true;
    return false;
  };

  const isCircleVideo = (name) => {
    return name && /^video_message_/i.test(name);
  };

  const isAudioFile = (name, url) => {
    const audioExts = /\.(ogg|mp3|wav|m4a|opus|aac)$/i;
    if (name && audioExts.test(name)) return true;
    // webm recorded as audio (filename starts with audio_)
    if (name && /\.webm$/i.test(name) && /^audio_/i.test(name)) return true;
    if (url && audioExts.test(url.split('?')[0])) return true;
    return false;
  };

  const uploadFile = async (file) => {
    const token = localStorage.getItem("token");
    const formData = new FormData();
    formData.append("file", file);
    const url = getApiUrl(`/api/community/chat-rooms/${roomSlug}/upload/`);
    const response = await fetch(url, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: formData });
    if (!response.ok) throw new Error("Upload failed");
    return await response.json();
  };

  const startRecording = async (type = 'video') => {
    try {
      const constraints = type === 'video'
        ? { video: { facingMode: 'user', width: 240, height: 240 }, audio: true }
        : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      recordingStreamRef.current = stream;
      recordingChunksRef.current = [];
      const mimeType = type === 'video'
        ? (MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ? 'video/webm;codecs=vp9' : 'video/webm')
        : (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus') ? 'audio/ogg;codecs=opus' : 'audio/webm');
      const recorder = new MediaRecorder(stream, { mimeType });
      recorder.ondataavailable = e => { if (e.data.size > 0) recordingChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        const ext = type === 'video' ? 'webm' : 'ogg';
        const mime = type === 'video' ? 'video/webm' : 'audio/ogg';
        const blob = new Blob(recordingChunksRef.current, { type: mime });
        const file = new File([blob], `${type}_message_${Date.now()}.${ext}`, { type: mime });
        setIsUploading(true);
        try {
          const result = await uploadFile(file);
          const fileMsg = `__FILE__:${JSON.stringify({ name: result.name, url: result.url, size: result.size })}`;
          if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ message: fileMsg }));
        } catch (_) {}
        finally { setIsUploading(false); }
      };
      mediaRecorderRef.current = recorder;
      recorder.start(100);
      setRecordingType(type);
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch (err) {
      console.error('Recording error:', err);
      setRecordingError('busy');
      setTimeout(() => setRecordingError(null), 3000);
    }
  };

  const stopRecording = () => {
    clearInterval(recordingTimerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (recordingStreamRef.current) {
      recordingStreamRef.current.getTracks().forEach(t => t.stop());
      recordingStreamRef.current = null;
    }
    setIsRecording(false);
    setRecordingTime(0);
  };

  const cancelRecording = () => {
    clearInterval(recordingTimerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
    }
    if (recordingStreamRef.current) {
      recordingStreamRef.current.getTracks().forEach(t => t.stop());
      recordingStreamRef.current = null;
    }
    recordingChunksRef.current = [];
    setIsRecording(false);
    setRecordingTime(0);
  };

  const formatRecTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;


  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setIsUploading(true);
    try {
      const result = await uploadFile(file);
      const fileMsg = `__FILE__:${JSON.stringify({ name: result.name, url: result.url, size: result.size })}`;
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ message: fileMsg }));
    } catch (_) {}
    finally { setIsUploading(false); }
  };

  const parseDate = (timestamp) => {
    if (!timestamp) return new Date();
    const s = String(timestamp);
    const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2})/);
    if (m) return new Date(`${m[3]}-${m[2]}-${m[1]}T${m[4]}:${m[5]}`);
    return new Date(s.replace(" ", "T"));
  };

  const formatTime = (timestamp) => {
    const date = parseDate(timestamp);
    if (isNaN(date.getTime())) return String(timestamp);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return "только что";
    if (diff < 3600000) return `${Math.floor(diff / 60000)} мин`;
    if (diff < 86400000) return date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
    return date.toLocaleDateString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  const shortTime = (timestamp) => {
    const date = parseDate(timestamp);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="text-chat">
      <div className="text-chat__header">
        <h3>💬 Чат</h3>
        <div className={`text-chat__status ${isConnected ? "connected" : "disconnected"}`}>
          <span className="text-chat__status-dot" />
          {isConnected ? "Онлайн" : "Офлайн"}
        </div>
      </div>

      <div className="text-chat__messages-wrapper">
        <div className="text-chat__messages" ref={messagesContainerRef} onScroll={handleScroll} data-lenis-prevent
          onTouchEnd={(e) => {
            // Dismiss actions if tap landed directly on the messages container (not on a message)
            if (e.target === messagesContainerRef.current) setHoveredMsgId(null);
          }}
        >
          {loadingMore && <div className="text-chat__loading-more">Загрузка...</div>}
          {hasMore && !loadingMore && (
            <button className="text-chat__load-more" onClick={loadMore}> Загрузить раньше</button>
          )}
          {isLoading ? (
            <div className="text-chat__loading">Загрузка сообщений...</div>
          ) : messages.length === 0 ? (
            <div className="text-chat__empty">
              <div className="text-chat__empty-icon"></div>
              <p>Нет сообщений</p>
              <p>Начните разговор!</p>
            </div>
          ) : (
            <>
              {messages.map((msg, index) => {
                const { username, avatar } = normalizeUser(msg.user);
                const isOwn = username === currentUser?.username;
                const prevMsg = index > 0 ? messages[index - 1] : null;
                const prevUsername = prevMsg ? normalizeUser(prevMsg.user).username : null;
                const timeDiff = prevMsg ? new Date(msg.created_at) - new Date(prevMsg.created_at) : Infinity;
                const isGrouped = prevUsername === username && timeDiff < 7 * 60 * 1000;
                const isDeleted = msg.is_deleted;

                const avatarUrl = avatar
                  ? (avatar.startsWith("http") ? avatar : `https://security.ncore.kg${avatar}`)
                  : `https://ui-avatars.com/api/?name=${encodeURIComponent(username)}&background=7C3AED&color=fff&bold=true`;

                const fileInfo = !isDeleted ? parseFileMessage(msg.content) : null;
                const replyData = msg.reply_to;

                return (
                  <div
                    key={msg.id}
                    className={`text-chat__message${isGrouped ? " grouped" : " group-start"}${isOwn ? " own" : ""}${msg.isOptimistic ? " optimistic" : ""}${isDeleted ? " deleted" : ""}`}
                    onMouseEnter={() => setHoveredMsgId(msg.id)}
                    onMouseLeave={() => setHoveredMsgId(null)}
                    onTouchStart={(e) => { e._touchStartY = e.touches[0]?.clientY; e._touchStartX = e.touches[0]?.clientX; }}
                    onTouchEnd={(e) => {
                      if (e.target.closest('button, a, video, input')) return;
                      const dy = Math.abs((e.changedTouches[0]?.clientY ?? 0) - (e._touchStartY ?? 0));
                      const dx = Math.abs((e.changedTouches[0]?.clientX ?? 0) - (e._touchStartX ?? 0));
                      if (dy > 10 || dx > 10) return; // was a scroll, not a tap
                      e.stopPropagation();
                      setHoveredMsgId(prev => prev === msg.id ? null : msg.id);
                    }}
                  >
                    <div className="text-chat__msg-left">
                      {!isGrouped ? (
                        <img src={avatarUrl} alt={username} className="text-chat__avatar"
                          onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(username)}&background=7C3AED&color=fff&bold=true`; }} />
                      ) : (
                        <span className="text-chat__hover-ts">{shortTime(msg.created_at)}</span>
                      )}
                    </div>

                    <div className="text-chat__message-content">
                      {!isGrouped && (
                        <div className="text-chat__message-header">
                          <span className={`text-chat__username${isOwn ? " own-name" : ""}`}>{username}</span>
                          <span className="text-chat__timestamp">{formatTime(msg.created_at)}</span>
                        </div>
                      )}

                      {/* Reply quote */}
                      {replyData && (() => {
                        const replyFile = parseFileMessage(replyData.content || '');
                        const replyIsImage = replyFile && isImageFile(replyFile.name, replyFile.url);
                        const replyIsCircle = replyFile && isCircleVideo(replyFile.name);
                        const replyIsVideo = replyFile && !replyIsCircle && isVideoFile(replyFile.name, replyFile.url);
                        const replyIsAudio = replyFile && isAudioFile(replyFile.name, replyFile.url);
                        return (
                          <div className="text-chat__reply-quote">
                            <span className="text-chat__reply-quote-name">↩ {replyData.username || replyData.user}</span>
                            {replyIsImage ? (
                              <div className="text-chat__reply-quote-img-wrap">
                                <img src={replyFile.url} alt={replyFile.name} className="text-chat__reply-quote-img" />
                                <span className="text-chat__reply-quote-text">🖼️ {replyFile.name}</span>
                              </div>
                            ) : replyIsCircle ? (
                              <div className="text-chat__reply-quote-img-wrap">
                                <div className="text-chat__reply-quote-circle-thumb">
                                  <video src={replyFile.url} preload="metadata" muted playsInline className="text-chat__reply-quote-circle-video" />
                                </div>
                                <span className="text-chat__reply-quote-text">⭕ Видео-кружок</span>
                              </div>
                            ) : replyIsVideo ? (
                              <div className="text-chat__reply-quote-img-wrap">
                                <div className="text-chat__reply-quote-video-thumb">
                                  <video src={replyFile.url} preload="metadata" muted playsInline className="text-chat__reply-quote-video-preview" />
                                </div>
                                <span className="text-chat__reply-quote-text">🎥 Видео</span>
                              </div>
                            ) : replyIsAudio ? (
                              <div className="text-chat__reply-quote-img-wrap">
                                <div className="text-chat__reply-quote-audio-thumb">
                                  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/></svg>
                                </div>
                                <span className="text-chat__reply-quote-text">🎤 Голосовое</span>
                              </div>
                            ) : (
                              <span className="text-chat__reply-quote-text">{String(replyData.content || "").slice(0, 80)}</span>
                            )}
                          </div>
                        );
                      })()}

                      <div className={`text-chat__message-text${isDeleted ? " text-chat__message-text--deleted" : ""}${fileInfo ? " text-chat__message-text--media" : ""}`}>
                        {fileInfo ? (
                          isImageFile(fileInfo.name, fileInfo.url) ? (
                            <div className="text-chat__image-wrap">
                              <img
                                src={fileInfo.url}
                                alt={fileInfo.name}
                                className="text-chat__image-preview"
                                onClick={() => setLightboxUrl(fileInfo.url)}
                                loading="lazy"
                              />
                              <a
                                href={fileInfo.url}
                                download={fileInfo.name}
                                className="text-chat__image-dl"
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Скачать"
                                onClick={e => e.stopPropagation()}
                              >
                                <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>
                              </a>
                            </div>
                          ) : isAudioFile(fileInfo.name, fileInfo.url) ? (
                            <VoiceMessage url={fileInfo.url} />
                          ) : isCircleVideo(fileInfo.name) ? (
                            <CircleVideoPlayer url={fileInfo.url} name={fileInfo.name} />
                          ) : isVideoFile(fileInfo.name, fileInfo.url) ? (
                            <div className="text-chat__video-wrap">
                              <video
                                src={fileInfo.url}
                                className="text-chat__video-preview"
                                controls
                                playsInline
                                preload="metadata"
                              />
                              <a
                                href={fileInfo.url}
                                download={fileInfo.name}
                                className="text-chat__image-dl"
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Скачать"
                              >
                                <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>
                              </a>
                            </div>
                          ) : (
                            <div className="text-chat__file-card">
                              <span className="text-chat__file-icon">📎</span>
                              <div className="text-chat__file-meta">
                                <span className="text-chat__file-name">{fileInfo.name}</span>
                                <span className="text-chat__file-size">{formatFileSize(fileInfo.size)}</span>
                              </div>
                              <a href={fileInfo.url} download={fileInfo.name} className="text-chat__file-dl" target="_blank" rel="noopener noreferrer" title="Скачать">
                                <svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>
                              </a>
                            </div>
                          )
                        ) : msg.content}
                      </div>

                      {/* Reactions — only show when they exist, no layout shift */}
                      {msg.reactions && msg.reactions.length > 0 && !isDeleted && (
                        <div className="text-chat__reactions">
                          {msg.reactions.map(r => (
                            <button key={r.emoji}
                              className={`text-chat__reaction${r.users.includes(currentUser?.username) ? " active" : ""}`}
                              onClick={() => handleReact(msg.id, r.emoji)}
                              title={r.users.join(", ")}>
                              {r.emoji} <span>{r.count}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Hover action bar — position:absolute, doesn't shift layout */}
                    {/* Keep visible while picker is open even if hover moved away */}
                    {(hoveredMsgId === msg.id || reactionPickerMsgId === msg.id) && !isDeleted && (
                      <div className="text-chat__actions">
                        <div className="text-chat__reaction-add-wrap">
                          <button className="text-chat__action-btn" onClick={() => setReactionPickerMsgId(prev => prev === msg.id ? null : msg.id)} title="Добавить реакцию">
                            <span style={{fontSize:'15px',lineHeight:1}}>😊</span>
                          </button>
                          {reactionPickerMsgId === msg.id && (
                            <ReactionPicker
                              msgId={msg.id}
                              onReact={handleReact}
                              onClose={() => setReactionPickerMsgId(null)}
                            />
                          )}
                        </div>
                        <button className="text-chat__action-btn" onClick={() => handleReply(msg)} title="Ответить">
                          <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z"/></svg>
                        </button>
                        {isOwn && (
                          <button className="text-chat__action-btn text-chat__action-btn--delete" onClick={() => handleDeleteMessage(msg.id)} title="Удалить">
                            <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>
        <div className="text-chat__scroll-btns">
          <button className="text-chat__scroll-btn" onClick={() => messagesContainerRef.current?.scrollBy({ top: -180, behavior: "smooth" })} {...holdUp}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
          </button>
          <button className="text-chat__scroll-btn" onClick={() => messagesContainerRef.current?.scrollBy({ top: 180, behavior: "smooth" })} {...holdDown}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
        </div>
      </div>

      {/* Reply preview bar */}
      {replyTo && (
        <div className="text-chat__reply-bar">
          <div className="text-chat__reply-bar-inner">
            <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" className="text-chat__reply-bar-icon"><path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z"/></svg>
            <div className="text-chat__reply-bar-content">
              <span className="text-chat__reply-bar-name">{replyTo.username}</span>
              <span className="text-chat__reply-bar-text">{String(replyTo.content).slice(0, 80)}</span>
            </div>
          </div>
          <button className="text-chat__reply-bar-close" onClick={() => setReplyTo(null)}>✕</button>
        </div>
      )}

      <div className="text-chat__input-container">
        {/* Hidden file input */}
        <input type="file" ref={fileInputRef} style={{ display: "none" }} accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.txt" onChange={handleFileSelect} />

        {/* 1. Attach button — desktop only */}
        <button type="button" className="text-chat__attach-btn" onClick={() => fileInputRef.current?.click()} disabled={!isConnected || isUploading} title="Прикрепить">
          {isUploading ? (
            <svg viewBox="0 0 24 24" fill="currentColor" className="text-chat__spin"><path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/></svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z"/></svg>
          )}
        </button>

        {/* 2. Input with emoji icon inside */}
        <div className="text-chat__input-wrap">
          <textarea
            ref={inputRef}
            className="text-chat__input"
            placeholder={replyTo ? `Ответить ${replyTo.username}...` : "Написать сообщение..."}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              const el = e.target;
              el.style.height = "auto";
              el.style.height = Math.min(el.scrollHeight, 140) + "px";
            }}
            onKeyDown={handleKeyPress}
            disabled={!isConnected}
            rows={1}
          />
          <div className="text-chat__input-emoji-slot">
            <EmojiPicker onSelect={(emoji) => setInputValue(prev => prev + emoji)} />
          </div>
        </div>

        {/* 3. Desktop: video + audio record buttons */}
        <div className="text-chat__circle-record-wrap text-chat__desk-only">
          {isRecording && (
            <div className="text-chat__rec-popup">
              {recordingType === 'video' ? (
                <div className="text-chat__rec-circle-wrap">
                  <video
                    ref={recordingPreviewCallback}
                    className="text-chat__rec-circle-video" autoPlay muted playsInline />
                  <div className="text-chat__rec-circle-ring" />
                  <div className="text-chat__rec-circle-badge">
                    <span className="text-chat__recording-dot" />
                    <span>{formatRecTime(recordingTime)}</span>
                  </div>
                </div>
              ) : (
                <div className="text-chat__rec-audio-ring">
                  <div className="text-chat__rec-audio-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40"><path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/></svg>
                  </div>
                  <div className="text-chat__rec-audio-badge">
                    <span className="text-chat__recording-dot" />
                    <span>{formatRecTime(recordingTime)}</span>
                  </div>
                </div>
              )}
              <p className="text-chat__rec-hint">{recordingType === 'video' ? 'Видео-кружок' : 'Голосовое'}</p>
              <div className="text-chat__rec-btns">
                <button className="text-chat__rec-cancel" onClick={cancelRecording} title="Отмена">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                </button>
                <button className="text-chat__rec-stop" onClick={stopRecording} title="Отправить">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M3.4 20.4l17.45-7.48a1 1 0 0 0 0-1.84L3.4 3.6a.993.993 0 0 0-1.39.91L2 9.12c0 .5.37.93.87.99L17 12 2.87 13.88c-.5.07-.87.5-.87 1l.01 4.51c0 .71.73 1.2 1.39.91z"/></svg>
                </button>
              </div>
            </div>
          )}
          {recordingError && (
            <div className="text-chat__rec-error">
              <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13"><path d="M12 1C5.9 1 1 5.9 1 12s4.9 11 11 11 11-4.9 11-11S18.1 1 12 1zm-.5 5h1v7h-1V6zm.5 11.5c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1z"/></svg>
              Устройство занято
            </div>
          )}
          {!isRecording && (
            <>
              <button
                type="button"
                className="text-chat__circle-record-btn"
                onClick={() => startRecording('video')}
                disabled={!isConnected || isUploading}
                title="Видео-кружок (записывает и аудио)"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17">
                  <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
                </svg>
              </button>
              <button
                type="button"
                className={`text-chat__circle-record-btn text-chat__circle-record-btn--audio${voiceMicActive ? ' text-chat__circle-record-btn--busy' : ''}`}
                onClick={() => startRecording('audio')}
                disabled={!isConnected || isUploading || voiceMicActive}
                title={voiceMicActive ? 'Микрофон занят голосовым чатом' : 'Голосовое сообщение'}
              >
                {voiceMicActive
                  ? <svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15"><path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2zm-5 3c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/><line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                  : <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17"><path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/></svg>
                }
              </button>
            </>
          )}
        </div>

        {/* 4. Desktop send button */}
        <button className="text-chat__send-btn text-chat__desk-only" onClick={sendMessage} disabled={!inputValue.trim() || !isConnected}>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M3.4 20.4l17.45-7.48a1 1 0 0 0 0-1.84L3.4 3.6a.993.993 0 0 0-1.39.91L2 9.12c0 .5.37.93.87.99L17 12 2.87 13.88c-.5.07-.87.5-.87 1l.01 4.51c0 .71.73 1.2 1.39.91z"/>
          </svg>
        </button>

        {/* 5. Mobile: single dynamic button (hidden on desktop) */}
        <div className="text-chat__mobile-record-wrap text-chat__mob-only">
          {recordingError && (
            <div className="text-chat__rec-error text-chat__rec-error--mobile">
              <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12"><path d="M12 1C5.9 1 1 5.9 1 12s4.9 11 11 11 11-4.9 11-11S18.1 1 12 1zm-.5 5h1v7h-1V6zm.5 11.5c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1z"/></svg>
              Занято
            </div>
          )}
          {isRecording && (
            <div className="text-chat__rec-popup">
              {recordingType === 'video' ? (
                <div className="text-chat__rec-circle-wrap">
                  <video
                    ref={recordingPreviewCallback}
                    className="text-chat__rec-circle-video" autoPlay muted playsInline />
                  <div className="text-chat__rec-circle-ring" />
                  <div className="text-chat__rec-circle-badge">
                    <span className="text-chat__recording-dot" />
                    <span>{formatRecTime(recordingTime)}</span>
                  </div>
                </div>
              ) : (
                <div className="text-chat__rec-audio-ring">
                  <div className="text-chat__rec-audio-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40"><path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/></svg>
                  </div>
                  <div className="text-chat__rec-audio-badge">
                    <span className="text-chat__recording-dot" />
                    <span>{formatRecTime(recordingTime)}</span>
                  </div>
                </div>
              )}
              <p className="text-chat__rec-hint">{recordingType === 'video' ? 'Видео-кружок' : 'Голосовое'}</p>
              <div className="text-chat__rec-btns">
                <button className="text-chat__rec-cancel" onClick={cancelRecording} title="Отмена">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                </button>
                <button className="text-chat__rec-stop" onClick={stopRecording} title="Отправить">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M3.4 20.4l17.45-7.48a1 1 0 0 0 0-1.84L3.4 3.6a.993.993 0 0 0-1.39.91L2 9.12c0 .5.37.93.87.99L17 12 2.87 13.88c-.5.07-.87.5-.87 1l.01 4.51c0 .71.73 1.2 1.39.91z"/></svg>
                </button>
              </div>
            </div>
          )}
          <button
            type="button"
            className={`text-chat__mobile-action-btn${inputValue.trim() ? ' is-send' : mobileRecordMode === 'video' ? ' is-video' : ' is-audio'}`}
            style={{ touchAction: 'none' }}
            onClick={() => { if (inputValue.trim()) sendMessage(); }}
            onTouchStart={(e) => {
              e.preventDefault();
              if (inputValue.trim()) { sendMessage(); return; }
              // For audio mode, mic is busy — show error toast and bail
              if (voiceMicActive && mobileRecordMode === 'audio') {
                setRecordingError('busy');
                setTimeout(() => setRecordingError(null), 3000);
                return;
              }
              mobileLongPressRef.current = setTimeout(() => {
                mobileLongPressRef.current = null;
                startRecording(mobileRecordMode);
              }, 500);
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              if (mobileLongPressRef.current) {
                clearTimeout(mobileLongPressRef.current);
                mobileLongPressRef.current = null;
                if (!inputValue.trim()) setMobileRecordMode(m => m === 'audio' ? 'video' : 'audio');
              }
            }}
            onTouchCancel={() => {
              if (mobileLongPressRef.current) {
                clearTimeout(mobileLongPressRef.current);
                mobileLongPressRef.current = null;
              }
            }}
            disabled={!isConnected || isUploading}
            title={inputValue.trim() ? 'Отправить' : mobileRecordMode === 'video' ? 'Удержать — видео / тап — переключить' : 'Удержать — голосовое / тап — переключить'}
          >
            {inputValue.trim() ? (
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M3.4 20.4l17.45-7.48a1 1 0 0 0 0-1.84L3.4 3.6a.993.993 0 0 0-1.39.91L2 9.12c0 .5.37.93.87.99L17 12 2.87 13.88c-.5.07-.87.5-.87 1l.01 4.51c0 .71.73 1.2 1.39.91z"/>
              </svg>
            ) : mobileRecordMode === 'video' ? (
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
              </svg>
            )}
          </button>
        </div>
      </div>
      <div className="text-chat__privacy">Чат защищён — сообщения не передаются третьим лицам.</div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div className="text-chat__lightbox" onClick={() => setLightboxUrl(null)}>
          <button className="text-chat__lightbox-close" onClick={() => setLightboxUrl(null)}>✕</button>
          <img
            src={lightboxUrl}
            alt="preview"
            className="text-chat__lightbox-img"
            onClick={e => e.stopPropagation()}
          />
          <a
            href={lightboxUrl}
            download
            className="text-chat__lightbox-dl"
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>
            Скачать
          </a>
        </div>
      )}
    </div>
  );
};

export default TextChat;